import { z } from "zod";

import { UserSubRoleEnum } from "../../User/enums/userRole";

import { CurrencyEnum } from "../../../enums/currencies.enum";

import { PlayerPositions } from "../../User/enums/playerPositions";

import { JobModeEnum, SalaryRateEnum, ApplyCriteriaEnum, IntroductoryVideoLengthEnum } from "../enum/jobEnums";
import { UserRoleEnum } from "../../User/enums";

const talentsBasicDetailsSchema = z.object({
  jobName: z.string({ required_error: "JOB_NAME_REQUIRED" }).min(1, { message: "JOB_NAME_REQUIRED" }),
  primaryPosition: z.nativeEnum(PlayerPositions, { required_error: "PRIMARY_POSITION_REQUIRED" }),
  secondaryPosition: z.nativeEnum(PlayerPositions, { required_error: "SECONDARY_POSITION_REQUIRED" }),
  jobMode: z.nativeEnum(JobModeEnum, { required_error: "JOB_MODE_REQUIRED" }),
  jobLocation: z.array(z.any()).min(1, { message: "JOB_LOCATION_REQUIRED" }),
  jobDescription: z
    .string({ required_error: "JOB_DESCRIPTION_REQUIRED" })
    .min(1, { message: "JOB_DESCRIPTION_REQUIRED" }),
  applyEnds: z.date({ required_error: "APPLY_ENDS_REQUIRED" }),
});

const compensationBenefitsSchema = z.object({
  salaryAmount: z.coerce
    .number({ required_error: "SALARY_AMOUNT_REQUIRED", invalid_type_error: "NUMERIC_VALUE_REQUIRED" })
    .min(1, { message: "SALARY_AMOUNT_REQUIRED" }),
  salaryCurrency: z.nativeEnum(CurrencyEnum, { required_error: "SALARY_CURRENCY_REQUIRED" }),
  salaryPer: z.nativeEnum(SalaryRateEnum, { required_error: "SALARY_PER_REQUIRED" }),
  isConfidential: z.boolean().optional().default(false),
});

const advancedTargetingSchema = z.object({
  howManyHiresNeeded: z.coerce
    .number({
      required_error: "HOW_MANY_HIRES_NEEDED_REQUIRED",
      invalid_type_error: "NUMERIC_VALUE_REQUIRED",
    })
    .min(1, { message: "HOW_MANY_HIRES_NEEDED_REQUIRED" }),
  nationality: z.array(z.any()).min(1, { message: "NATIONALITY_REQUIRED" }),
  talentProfessionalLocation: z.array(z.any()).min(1, { message: "TALENT_PROFESSIONAL_LOCATION_REQUIRED" }),
  [ApplyCriteriaEnum.onlyVerifiedFootballers]: z.boolean().optional(),
  [ApplyCriteriaEnum.onlyWithTestimonials]: z.boolean().optional(),
  [ApplyCriteriaEnum.profileCompletionAbove80]: z.boolean().optional(),
});

const requiredSubmissionInfoSchema = z.object({
  isLanguagesSpokenActive: z.boolean().optional(),
  languagesSpoken: z.array(z.string({ required_error: "LANGUAGES_SPOKEN_REQUIRED" })).optional(),
  majorInjury: z.boolean().optional().default(false),
  willingnessToRelocate: z.boolean().optional().default(false),
  isIntroductoryVideoActive: z.boolean().optional(),
  introductoryVideo: z
    .object({
      description: z.string({ required_error: "INTRODUCTORY_VIDEO_DESCRIPTION_REQUIRED" }),
      length: z.nativeEnum(IntroductoryVideoLengthEnum, { required_error: "INTRODUCTORY_VIDEO_LENGTH_REQUIRED" }),
    })
    .optional(),
  isClubsWorkedInActive: z.boolean().optional(),
  clubsWorkedIn: z
    .array(z.any()) // Accept Entity objects for UI purposes
    .min(1, { message: "CLUBS_WORKED_IN_REQUIRED" })
    .optional(),
});

const externalLinkSchema = z.object({
  isExternal: z.boolean().default(false),
  externalLink: z.string().url({ message: "INVALID_URL" }).optional(),
});

const talentsJobBaseSchema = talentsBasicDetailsSchema
  .merge(compensationBenefitsSchema)
  .merge(advancedTargetingSchema)
  .merge(requiredSubmissionInfoSchema)
  .merge(externalLinkSchema);

export const talentsJobSchema = talentsJobBaseSchema.superRefine((data, ctx) => {
  if (data.isExternal && !data.externalLink) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "EXTERNAL_LINK_REQUIRED",
      path: ["externalLink"],
    });
  }
});

export const createTalentJobSchema = talentsJobSchema;

const professionalsBasicDetailsSchema = z.object({
  jobName: z.string({ required_error: "JOB_NAME_REQUIRED" }).min(1, { message: "JOB_NAME_REQUIRED" }),
  position: z.nativeEnum(UserRoleEnum, { required_error: "POSITION_REQUIRED" }),
  subRole: z.nativeEnum(UserSubRoleEnum).optional(),
  jobMode: z.nativeEnum(JobModeEnum, { required_error: "JOB_MODE_REQUIRED" }),
  jobLocation: z.array(z.any()).min(1, { message: "JOB_LOCATION_REQUIRED" }),
  jobDescription: z
    .string({ required_error: "JOB_DESCRIPTION_REQUIRED" })
    .min(1, { message: "JOB_DESCRIPTION_REQUIRED" }),
  applyEnds: z.date({ required_error: "APPLY_ENDS_REQUIRED" }),
});

const professionalsRequiredSubmissionInfoBaseSchema = z.object({
  isLanguagesSpokenActive: z.boolean().optional(),
  languagesSpoken: z.array(z.string({ required_error: "LANGUAGES_SPOKEN_REQUIRED" })).optional(),
  willingnessToRelocate: z.boolean().optional().default(false),
  isIntroductoryVideoActive: z.boolean().optional(),
  introductoryVideo: z
    .object({
      description: z.string({ required_error: "INTRODUCTORY_VIDEO_DESCRIPTION_REQUIRED" }),
      length: z.nativeEnum(IntroductoryVideoLengthEnum, { required_error: "INTRODUCTORY_VIDEO_LENGTH_REQUIRED" }),
    })
    .optional(),
  isClubsWorkedInActive: z.boolean().optional(),
  clubsWorkedIn: z
    .array(z.any()) // Accept Entity objects for UI purposes
    .optional(),
  isCountriesScoutedInActive: z.boolean().optional(),
  countriesScoutedIn: z.array(z.any()).optional(),
  isCertificateAndLicensesActive: z.boolean().optional(),
  certificateAndLicenses: z.array(z.string()).optional(),
  isCountriesCoachedInActive: z.boolean().optional(),
  countriesCoachedIn: z.array(z.any()).optional(),
  totalCareerTransfers: z.boolean().optional(),
});

const professionalsJobBaseSchema = professionalsBasicDetailsSchema
  .merge(compensationBenefitsSchema)
  .merge(advancedTargetingSchema)
  .merge(professionalsRequiredSubmissionInfoBaseSchema)
  .merge(externalLinkSchema);

export const professionalsJobSchema = professionalsJobBaseSchema.superRefine((data, ctx) => {
  if (data.position === UserRoleEnum.OTHER && !data.subRole) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "SUB_ROLE_REQUIRED",
      path: ["subRole"],
    });
  }
  if (data.isCountriesCoachedInActive && !data.countriesCoachedIn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "COUNTRIES_COACHED_IN_REQUIRED",
      path: ["countriesCoachedIn"],
    });
  }
  if (data.isCountriesScoutedInActive && !data.countriesScoutedIn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "COUNTRIES_SCOUTED_IN_REQUIRED",
      path: ["countriesScoutedIn"],
    });
  }
  if (data.isCertificateAndLicensesActive && !data.certificateAndLicenses) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "CERTIFICATE_AND_LICENSES_REQUIRED",
      path: ["certificateAndLicenses"],
    });
  }
  if (data.isClubsWorkedInActive && !data.clubsWorkedIn) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "CLUBS_WORKED_IN_REQUIRED", path: ["clubsWorkedIn"] });
  }
  if (data.isExternal && !data.externalLink) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "EXTERNAL_LINK_REQUIRED",
      path: ["externalLink"],
    });
  }
});

export const createProfessionalJobSchema = professionalsJobSchema;
